/**
 * PHARMAKON — pricing engine · integer-centime money arithmetic.
 *
 * Every amount of the engine is an INTEGER number of centimes (1 DA = 100 centimes). Nothing is
 * ever computed on a binary float: inputs (JS numbers, decimal strings, Prisma.Decimal) are parsed
 * from their decimal text, products and quotients run on BigInt, and every stored amount is rounded
 * exactly once, half away from zero (« arrondi commercial », 03_DATABASE P-7, REVENUE_MARGIN §4).
 * Allocations use the largest-remainder method so that the parts always sum to the whole.
 *
 * Canonical source: packages/shared/pricing/. The API image is built from apps/server alone, so
 * apps/server/src/utils/pricing/ holds a byte-identical copy (`npm run sync:pricing` in apps/server);
 * apps/server/src/__tests__/pricing.parity.test.js fails on any drift. Edit here, then sync.
 * This folder must stay dependency-free (relative imports inside the folder only).
 */

/** DZD amounts carry two decimals (03_DATABASE P-7: `Decimal(…, 2)`). */
export const ECHELLE_MONETAIRE = 2;
/** Percentages (UG %, TVA, remise %, incentive %) are handled in hundredths of a percent. */
export const ECHELLE_POURCENTAGE = 2;
/** Stored margin percentages (`DetailAchat.marge_pct Decimal(7,4)`) keep four decimals. */
export const ECHELLE_MARGE_PCT = 4;

const DECIMAL_RE = /^([+-])?(\d*)(?:\.(\d*))?$/;

function texteDecimal(valeur) {
  if (typeof valeur === "number") {
    if (!Number.isFinite(valeur)) throw new TypeError(`Montant non fini : ${valeur}`);
    // Shortest round-trip text of the double: 0.1 → "0.1", 1.005 → "1.005" (never 1.00499…).
    const t = String(valeur);
    if (/e/i.test(t)) {
      // Exponent form only happens for |x| < 1e-6 or ≥ 1e21: normalise through toFixed.
      if (Math.abs(valeur) >= 1e21) throw new RangeError(`Montant hors bornes : ${valeur}`);
      return valeur.toFixed(20);
    }
    return t;
  }
  if (typeof valeur === "bigint") return valeur.toString();
  if (typeof valeur === "string") return valeur.trim().replace(",", ".");
  if (valeur && typeof valeur === "object" && typeof valeur.toFixed === "function") {
    // Prisma.Decimal / decimal.js: toFixed() never uses the exponent form.
    return valeur.toFixed();
  }
  throw new TypeError(`Montant illisible : ${String(valeur)}`);
}

function versBigInt(n) {
  if (typeof n === "bigint") return n;
  if (!Number.isSafeInteger(n)) throw new RangeError(`Entier hors bornes sûres : ${n}`);
  return BigInt(n);
}

function versNombreSur(b) {
  const n = Number(b);
  if (!Number.isSafeInteger(n)) throw new RangeError(`Résultat hors bornes sûres : ${b}`);
  return n;
}

/**
 * Division of two integers rounded half away from zero. BigInt in, safe integer out.
 * @param {bigint|number} numerateur
 * @param {bigint|number} denominateur non-zero
 */
export function diviserArrondi(numerateur, denominateur) {
  const n = versBigInt(numerateur);
  const d = versBigInt(denominateur);
  if (d === 0n) throw new RangeError("Division par zéro");
  const negatif = (n < 0n) !== (d < 0n);
  const an = n < 0n ? -n : n;
  const ad = d < 0n ? -d : d;
  const q = (2n * an + ad) / (2n * ad);
  return versNombreSur(negatif ? -q : q);
}

/** Floor division of two integers (toward −∞). */
export function diviserPlancher(numerateur, denominateur) {
  const n = versBigInt(numerateur);
  const d = versBigInt(denominateur);
  if (d === 0n) throw new RangeError("Division par zéro");
  let q = n / d;
  if ((n % d !== 0n) && ((n < 0n) !== (d < 0n))) q -= 1n;
  return versNombreSur(q);
}

/**
 * Parses a decimal value into an integer at the given scale, rounding half away from zero once.
 * `enEntierEchelle("12.345", 2)` → 1235 ; `enEntierEchelle(null)` → 0.
 * null / undefined / "" read as 0 (an empty field of a line being typed); anything else that is not
 * a decimal number throws a TypeError.
 */
export function enEntierEchelle(valeur, echelle) {
  if (valeur === null || valeur === undefined || valeur === "") return 0;
  const texte = texteDecimal(valeur);
  const m = DECIMAL_RE.exec(texte);
  if (!m || (m[2] === "" && (m[3] === undefined || m[3] === ""))) {
    throw new TypeError(`Montant illisible : ${String(valeur)}`);
  }
  const signe = m[1] === "-" ? -1n : 1n;
  const entier = m[2] === "" ? "0" : m[2];
  const frac = m[3] ?? "";
  const garde = frac.padEnd(echelle + 1, "0");
  const brut = BigInt(entier + garde.slice(0, echelle + 1)); // one extra digit
  let q = brut / 10n;
  const chiffre = brut % 10n;
  // Half away from zero on the magnitude: the guard digit alone decides (digits after it cannot
  // move a value across the half-way point).
  if (chiffre >= 5n) q += 1n;
  return versNombreSur(signe * q);
}

/** DA value (number | string | Prisma.Decimal) → integer centimes. */
export function versCentimes(valeur) {
  return enEntierEchelle(valeur, ECHELLE_MONETAIRE);
}

/** Percentage (number | string | Decimal) → integer hundredths of a percent (5 % → 500). */
export function versCentiemesPct(valeur) {
  return enEntierEchelle(valeur, ECHELLE_POURCENTAGE);
}

/** Integer at a scale → exact decimal string, e.g. (123456, 2) → "1234.56", (-5, 2) → "-0.05". */
export function versTexteDecimal(entier, echelle = ECHELLE_MONETAIRE) {
  const b = versBigInt(entier);
  const negatif = b < 0n;
  const abs = (negatif ? -b : b).toString().padStart(echelle + 1, "0");
  const texte = echelle === 0 ? abs : `${abs.slice(0, -echelle)}.${abs.slice(-echelle)}`;
  return negatif ? `-${texte}` : texte;
}

/** Integer centimes → DA as a JS number (display / JSON only; persist through versTexteDecimal). */
export function versDinars(centimes) {
  return Number(versTexteDecimal(centimes, ECHELLE_MONETAIRE));
}

/** Integer hundredths of a percent → percentage as a JS number (500 → 5). */
export function versPct(centiemes) {
  return Number(versTexteDecimal(centiemes, ECHELLE_POURCENTAGE));
}

/**
 * amount × percentage, rounded once to the centime. `pctCentiemes` is in hundredths of a percent.
 * appliquerPct(294000, 900) → 26460 (2 940,00 DA × 9 % = 264,60 DA).
 */
export function appliquerPct(centimes, pctCentiemes) {
  return diviserArrondi(versBigInt(centimes) * versBigInt(pctCentiemes), 10000n);
}

/**
 * Ratio `numerateur / denominateur × 100` as a percentage rounded half away from zero to `echelle`
 * decimals, returned as a JS number; null when the denominator is 0 (undefined ratio).
 */
export function ratioPct(numerateur, denominateur, echelle = ECHELLE_MARGE_PCT) {
  const d = versBigInt(denominateur);
  if (d === 0n) return null;
  const facteur = 100n * 10n ** BigInt(echelle);
  const entier = diviserArrondi(versBigInt(numerateur) * facteur, d);
  return Number(versTexteDecimal(entier, echelle));
}

/** Product of two integers (quantity × unit amount), safe-integer checked. */
export function multiplier(a, b) {
  return versNombreSur(versBigInt(a) * versBigInt(b));
}

/** Σ of integer centimes (safe-integer checked). */
export function somme(valeurs) {
  let s = 0n;
  for (const v of valeurs) s += versBigInt(v);
  return versNombreSur(s);
}

/**
 * Largest-remainder (Hamilton) allocation of an integer `total` over non-negative integer `poids`.
 * Each part is floor(total × w_i / Σw); the leftover units go one by one to the largest fractional
 * remainders — ties to the larger weight, then to the earlier index — so Σ parts = total exactly
 * (03_DATABASE P-7). A negative total is allocated on its magnitude and the sign put back.
 * Σw = 0 is only allowed with total = 0 (every part 0); otherwise a RangeError is thrown.
 * @param {number} total integer
 * @param {number[]} poids non-negative integers
 * @returns {number[]}
 */
export function repartirPlusFortsRestes(total, poids) {
  const t = versBigInt(total);
  const w = poids.map((p) => {
    const b = versBigInt(p);
    if (b < 0n) throw new RangeError(`Poids négatif : ${p}`);
    return b;
  });
  const sw = w.reduce((a, b) => a + b, 0n);
  if (sw === 0n) {
    if (t !== 0n) throw new RangeError("Répartition impossible : somme des poids nulle");
    return w.map(() => 0);
  }
  const negatif = t < 0n;
  const at = negatif ? -t : t;
  const parts = w.map((wi) => (at * wi) / sw);
  const restes = w.map((wi, i) => ({ i, r: (at * wi) % sw, wi }));
  let reste = at - parts.reduce((a, b) => a + b, 0n);
  restes.sort((a, b) => {
    if (a.r !== b.r) return a.r > b.r ? -1 : 1;
    if (a.wi !== b.wi) return a.wi > b.wi ? -1 : 1;
    return a.i - b.i;
  });
  for (let k = 0; reste > 0n; k += 1, reste -= 1n) parts[restes[k].i] += 1n;
  return parts.map((p) => versNombreSur(negatif ? -p : p));
}
